/**
 * Thin fetch wrapper for talking to the backend (Cloudflare Worker /
 * Pages Functions under /functions/api). Not used by the dummy
 * services yet, but every real implementation should go through this
 * so auth headers, error handling, and the base URL are defined once.
 *
 * VITE_API_BASE_URL is read from the environment (see .env.example).
 * Leave it empty during local dev with the dummy services; set it to
 * your deployed Worker URL (or leave empty and rely on same-origin
 * Pages Functions at /api/...) once Google Calendar sync is wired up.
 */
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';
const REQUEST_TIMEOUT_MS = 15000;

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = 'ApiError';
    this.status = status; // 0 = network error / timeout (no HTTP response at all)
  }
}

export async function apiFetch(path, options = {}) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      credentials: 'include',
      ...options,
      signal: controller.signal,
    });
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new ApiError('通信がタイムアウトしました。もう一度お試しください。', 0);
    }
    throw new ApiError('通信エラーが発生しました。ネットワークをご確認ください。', 0);
  } finally {
    clearTimeout(timeoutId);
  }

  if (!res.ok) {
    // functions/api/events.js returns { message, detail } JSON on errors —
    // surface that message directly so toasts stay short and readable
    // instead of dumping a raw response body.
    let message = `保存に失敗しました（status ${res.status}）`;
    try {
      const body = await res.clone().json();
      if (body?.message) message = body.message;
    } catch {
      const text = await res.text().catch(() => '');
      if (text) message = text;
    }
    throw new ApiError(message, res.status);
  }

  if (res.status === 204) return null;
  return res.json();
}
